import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { DigestPage } from '@/features/digest/DigestPage';
import { parseAsOf } from '@/lib/asof/asof';
import { isMarket, type Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  `/:market/digest` —— ARCHITECTURE §10.1【畫面 2】、ROADMAP §6。
 *
 *  `?date=` 是 typed search param，因為它是**產品狀態**：「把昨天那份摘要貼給同事」
 *  必須可用 URL 定址（§2.6 路由那一列）。`?as_of` 在 `__root.tsx` 宣告，這裡繼承。
 *
 *  兩者的分工：`?date=` 明說要看哪一份；沒帶時退回 `?as_of`（時光機倒帶到
 *  2020-03-15 就看那天早上的摘要），再沒有就讓後端回最近一份。
 *  那個退回邏輯在 `features/digest/digest-view.ts`，有測試。
 *
 *  **這裡沒有 `key={...}`**（§10.2 規則 3）：換一天摘要時整棵子樹不重建，
 *  所以捲軸位置不會跳回頂端。
 * ========================================================================== */

export interface DigestSearch {
  date?: string;
}

export const Route = createFileRoute('/$market/digest')({
  validateSearch: (search: Record<string, unknown>): DigestSearch => {
    // 日期的合法性與 as_of 同一條（`parseAsOf`）。不合法就整個丟掉而不是報錯：
    // 一個被截斷的分享連結應該退化成「最近一份」，不該變成錯誤頁。
    const date = parseAsOf(search['date']);
    return date === undefined ? {} : { date };
  },
  component: DigestRoute,
});

function DigestRoute() {
  const { market: rawMarket } = Route.useParams();
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const market: Market = isMarket(rawMarket) ? rawMarket : 'tw';

  return (
    <DigestPage
      market={market}
      date={date}
      onDateChange={(next) => {
        void navigate({
          to: '/$market/digest',
          params: { market },
          search: (prev: Record<string, unknown>) => {
            const { date: _drop, ...rest } = prev;
            return next === undefined ? rest : { ...rest, date: next };
          },
        });
      }}
    />
  );
}
